import type { PhysicalAddress } from '@/features/tax-profile/PhysicalAddress'

/**
 * Formats a physical address into a readable string.
 * @param address The physical address to format (can be partial)
 * @returns A formatted address string, or null if no address parts are provided
 */
export function formatPhysicalAddress(
  address: Partial<PhysicalAddress> | null,
): string | null {
  if (!address) {
    return null
  }

  const { addressLine1, addressLine2, city, region, postalCode, country } =
    address

  // Check if any address parts exist
  const hasAddress = addressLine1 || city || region || postalCode || country
  if (!hasAddress) {
    return null
  }

  const addressParts = []

  // Add street address lines
  if (addressLine1) {
    addressParts.push(addressLine1)
  }

  if (addressLine2) {
    addressParts.push(addressLine2)
  }

  // Format city, state, zip
  const cityStateZip = [city, region].filter(Boolean).join(', ')
  const fullCityStateZip = postalCode
    ? cityStateZip
      ? `${cityStateZip} ${postalCode}`
      : postalCode
    : cityStateZip

  if (fullCityStateZip) {
    addressParts.push(fullCityStateZip)
  }

  if (country) {
    addressParts.push(country)
  }

  return addressParts.join(', ')
}
